/*
 * Taught-aliases list modal (batch 5 #7) — the management view over the
 * concept-alias ledger. Every row is an operator-taught "ticket concept →
 * master-sheet CONCEPT" mapping the matcher resolves as its top tier; a stale
 * or wrong one silently steers launches to the wrong row, so the list keeps
 * them visible (who taught what, when) with a one-click delete (operator).
 */

import { useState } from "react";
import {
  Alert as MantineAlert,
  Badge,
  Button as MantineButton,
  Group,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { Table, type Column } from "../ui";
import { useAuth } from "../../auth/AuthContext";
import { useConceptAliases, useDeleteConceptAlias } from "../../api/queries";
import { errorMessage } from "../../api/errors";
import type { ConceptAliasRow } from "../../api/client";
import { formatDateTime } from "../../lib/format";

export interface AliasListModalProps {
  open: boolean;
  onClose: () => void;
}

export function AliasListModal({ open, onClose }: AliasListModalProps) {
  const { can } = useAuth();
  const aliasesQuery = useConceptAliases("", { enabled: open });
  const del = useDeleteConceptAlias();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const aliases = aliasesQuery.data?.aliases ?? [];

  const columns: Array<Column<ConceptAliasRow>> = [
    {
      key: "mapping",
      header: "Alias",
      render: (a) => (
        <Stack gap={0}>
          <Text component="span" fz="sm">
            {a.input_raw}
          </Text>
          <Text component="span" fz="xs" c="dimmed">
            → {a.canonical_concept}
          </Text>
        </Stack>
      ),
    },
    {
      key: "product",
      header: "Product",
      render: (a) => a.product_key,
      width: "90px",
    },
    {
      key: "taught",
      header: "Taught",
      render: (a) => (
        <Text component="span" fz="xs" c="dimmed">
          {a.created_by ?? "—"} · {formatDateTime(a.created_at)}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (a) =>
        can("operate") ? (
          <MantineButton
            variant="subtle"
            color="red"
            size="compact-xs"
            loading={del.isPending && deletingId === a.id}
            onClick={() => {
              setDeletingId(a.id);
              del.mutate(a.id);
            }}
          >
            Delete
          </MantineButton>
        ) : null,
      width: "80px",
    },
  ];

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Group gap="xs">
          <span>Taught concept aliases</span>
          <Badge variant="light">{aliases.length}</Badge>
        </Group>
      }
      centered
      size="xl"
    >
      <Stack gap="sm">
        <Text c="dimmed" fz="sm">
          Ticket concepts the matcher resolves by an operator-taught mapping
          (before any fuzzy tier). Delete a row to fall back to normal matching.
        </Text>

        {aliasesQuery.isError && (
          <MantineAlert color="red" variant="light" role="alert">
            Couldn't load aliases: {errorMessage(aliasesQuery.error)}
          </MantineAlert>
        )}
        {del.isError && (
          <MantineAlert color="red" variant="light" role="alert">
            Couldn't delete the alias: {errorMessage(del.error)}
          </MantineAlert>
        )}

        {aliasesQuery.isLoading ? (
          <Text c="dimmed" fz="sm">
            Loading…
          </Text>
        ) : aliases.length === 0 ? (
          <Text c="dimmed" fz="sm">
            No aliases taught yet — teach one from a "no sheet row" hygiene flag
            or a failed run ticket.
          </Text>
        ) : (
          <Table columns={columns} rows={aliases} rowKey={(a) => a.id} />
        )}
      </Stack>
    </Modal>
  );
}
